// frontend/src/pages/tools/DosingCalculatorPage.tsx
import { useEffect, useState, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  TextInput,
  NumberInput,
  Button,
  Paper,
  Divider,
  Notification,
} from '@mantine/core';
import {
  IconPlus,
  IconDownload,
  IconCheck,
  IconCopy,
  IconX,
} from '@tabler/icons-react';
import { usePublicDosingStore } from '@/stores/publicDosingStore';
import { ContainerCard } from '@/components/public-dosing/ContainerCard';
import { LeadGenBanner } from '@/components/public-dosing/LeadGenBanner';
import { generateBatchPdf } from '@/utils/generatePdf';
import { syncBatchToUrl } from '@/utils/dosingUrlSync';
// FIX C1: Import from centralized analytics utility
import { trackEvent } from '@/utils/analytics';

export function DosingCalculatorPage() {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Track created meta tags for cleanup
  const createdMetaTagsRef = useRef<HTMLMetaElement[]>([]);

  const batch = usePublicDosingStore((state) => state.batch);
  const setBatchName = usePublicDosingStore((state) => state.setBatchName);
  const setBatchDays = usePublicDosingStore((state) => state.setBatchDays);
  const addContainer = usePublicDosingStore((state) => state.addContainer);
  const updateContainer = usePublicDosingStore((state) => state.updateContainer);
  const removeContainer = usePublicDosingStore((state) => state.removeContainer);
  const addElement = usePublicDosingStore((state) => state.addElement);
  const updateElement = usePublicDosingStore((state) => state.updateElement);
  const removeElement = usePublicDosingStore((state) => state.removeElement);
  const getBatchTotal = usePublicDosingStore((state) => state.getBatchTotal);
  const initFromUrl = usePublicDosingStore((state) => state.initFromUrl);
  const reset = usePublicDosingStore((state) => state.reset);

  // Initialize from URL on mount
  useEffect(() => {
    initFromUrl();
  }, [initFromUrl]);

  // FIX C6: Robust SEO meta tag handling with proper cleanup
  useEffect(() => {
    const pageTitle = 'Free Trace Element Dosing Calculator | Reef Data Hub';
    const pageDescription = 'Calculate trace element dosing for your reef tank. Create multi-container batches, export to PDF. Free, no signup required.';

    // Sanitize URL to prevent XSS - only allow valid URL characters
    const sanitizeUrl = (url: string): string => {
      try {
        const parsed = new URL(url);
        // Only allow http/https protocols
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          return window.location.origin + '/tools/dosing-calculator';
        }
        return parsed.origin + parsed.pathname;
      } catch {
        return window.location.origin + '/tools/dosing-calculator';
      }
    };

    // Store original title for restoration
    const originalTitle = document.title;

    // Set page title
    document.title = pageTitle;

    // Track created tags for cleanup
    const createdTags: HTMLMetaElement[] = [];

    // Set meta description - create if doesn't exist
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    const originalMetaDescContent = metaDesc?.getAttribute('content') ?? null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
      createdTags.push(metaDesc);
    }
    metaDesc.setAttribute('content', pageDescription);

    // Set Open Graph tags for social sharing
    const ogTags: Array<{ property: string; content: string }> = [
      { property: 'og:title', content: pageTitle },
      { property: 'og:description', content: pageDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: sanitizeUrl(window.location.href) },
    ];

    const originalOgValues: Map<string, string | null> = new Map();

    ogTags.forEach(({ property, content }) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      originalOgValues.set(property, meta?.getAttribute('content') ?? null);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
        createdTags.push(meta);
      }
      meta.setAttribute('content', content);
    });

    // Store created tags for cleanup
    createdMetaTagsRef.current = createdTags;

    // Track page view
    trackEvent('page_view', {
      page_title: 'Public Dosing Calculator',
      page_location: window.location.href,
      page_path: '/tools/dosing-calculator',
    });

    // Cleanup function to restore original meta on unmount
    return () => {
      // Restore original title
      document.title = originalTitle;

      // Remove tags we created
      createdTags.forEach((tag) => {
        if (tag.parentNode) {
          tag.parentNode.removeChild(tag);
        }
      });

      // Restore original values for tags we modified
      if (originalMetaDescContent !== null) {
        const metaDesc = document.querySelector('meta[name="description"]');
        metaDesc?.setAttribute('content', originalMetaDescContent);
      }

      originalOgValues.forEach((originalValue, property) => {
        if (originalValue !== null) {
          const meta = document.querySelector(`meta[property="${property}"]`);
          meta?.setAttribute('content', originalValue);
        }
      });
    };
  }, []);

  const handleExportPdf = async () => {
    if (batch.containers.length === 0) return;

    setPdfError(null);
    setIsPdfLoading(true);
    try {
      await generateBatchPdf(batch);
    } catch (error) {
      console.error('PDF generation failed:', error);
      setPdfError('Failed to generate PDF. Please try again.');
    } finally {
      setIsPdfLoading(false);
    }
  };

  const handleShare = () => {
    trackEvent('share', {
      method: 'copy_link',
      content_type: 'dosing_batch',
      item_id: batch.name,
    });
  };

  const handleAddContainer = () => {
    addContainer();
    trackEvent('select_content', {
      content_type: 'container',
      item_id: 'add_container',
    });
  };

  const handleReset = () => {
    reset();
    trackEvent('select_content', {
      content_type: 'action',
      item_id: 'reset_batch',
    });
  };

  // FIX A5: Manual clipboard handling to avoid CopyButton race condition
  // CopyButton's value prop is evaluated at render time, not when copy() executes
  // By reading window.location.href after flush, we get the updated URL
  const handleCopyLink = async () => {
    syncBatchToUrl.flush();
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      handleShare();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const batchTotal = getBatchTotal();

  return (
    <Container size="lg" py="xl">
        <Stack gap="lg">
          {/* PDF Error Notification */}
          {pdfError && (
            <Notification
              icon={<IconX size={18} />}
              color="red"
              title="PDF Export Failed"
              onClose={() => setPdfError(null)}
            >
              {pdfError}
            </Notification>
          )}

          {/* Lead Gen Banner */}
          <LeadGenBanner />

          {/* Page Header */}
          <div>
            <Title order={1}>Trace Element Dosing Calculator</Title>
            <Text c="dimmed" mt="xs">
              Create custom dosing batches for your reef tank. No signup required.
            </Text>
          </div>

          {/* Batch Settings */}
          <Paper p="md" radius="md" withBorder>
            <Group gap="md" align="flex-end">
              <TextInput
                label="Batch Name"
                value={batch.name}
                onChange={(e) => setBatchName(e.target.value)}
                style={{ flex: 1, maxWidth: 300 }}
                aria-label="Batch name"
              />
              <NumberInput
                label="Days"
                value={batch.days}
                onChange={(val) => setBatchDays(val)}
                min={1}
                max={365}
                style={{ width: 100 }}
                aria-label="Batch duration in days"
              />
              <Text size="sm" c="dimmed" pb={8}>
                Total: <strong>{batchTotal.toFixed(2)} ml</strong>
              </Text>
            </Group>
          </Paper>

          {/* Containers */}
          {batch.containers.length > 0 ? (
            <Stack gap="md">
              {batch.containers.map((container, index) => (
                <ContainerCard
                  key={index}
                  container={container}
                  index={index}
                  days={batch.days}
                  onUpdate={updateContainer}
                  onRemove={removeContainer}
                  onAddElement={addElement}
                  onUpdateElement={updateElement}
                  onRemoveElement={removeElement}
                />
              ))}
            </Stack>
          ) : (
            <Paper p="xl" radius="md" withBorder ta="center">
              <Text c="dimmed" size="lg">
                Add a container to get started
              </Text>
              <Text c="dimmed" size="sm" mt="xs">
                Each container represents a separate dosing solution
              </Text>
            </Paper>
          )}

          {/* Actions */}
          <Group justify="space-between" wrap="wrap" gap="md">
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={handleAddContainer}
              variant="light"
            >
              Add Container
            </Button>

            <Group gap="sm" wrap="wrap">
              <Button variant="subtle" color="gray" onClick={handleReset} size="sm">
                Reset
              </Button>

              <Button
                leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                variant="light"
                onClick={handleCopyLink}
                color={copied ? 'teal' : undefined}
                size="sm"
              >
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>

              <Button
                leftSection={<IconDownload size={16} />}
                onClick={handleExportPdf}
                loading={isPdfLoading}
                disabled={batch.containers.length === 0}
                size="sm"
              >
                Export PDF
              </Button>
            </Group>
          </Group>

          {/* Footer info */}
          <Divider my="lg" />
          <Text size="xs" c="dimmed" ta="center">
            This calculator is provided free by Reef Data Hub. Your data stays in your
            browser and is encoded in the URL for easy sharing.
          </Text>
          <Text size="sm" c="dimmed" ta="center" mt="md">
            Reef Data Hub is a modern tool to integrate, track, and analyze data from all
            sources connected to your reef tank.
          </Text>
        </Stack>
      </Container>
  );
}
